import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useCreateServiceCenter, useUpdateServiceCenter, useCity, useArea, useSubArea, usePremise, useServiceCenters } from "../../hooks";
import { CreateServiceCenterPayload } from "../../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface ServiceCenter {
  id: string;
  name: string;
  address: string;
  city: string;
  cityCode: string;        // Add this
  phone: string;
  email: string;
  areas: string[];
  areaCodes: string[];     // Add this
  subAreas: string[];
  subAreaCodes: string[];  // Add this
  premises: string[];
  premiseCodes: string[];  // Add this
  services: string[];
  isActive: boolean;
  type: string;
  description: any;
}

interface ServiceCenterFormProps {
  center: ServiceCenter | null;
  serviceTypes: string[];
  availableServices: string[];
  onSave: (center: Partial<ServiceCenter>) => void;
  onCancel: () => void;
  onRefetch?: () => void;
}

export const ServiceCenterFormDialog = ({ 
  center, 
  serviceTypes, 
  availableServices, 
  onSave, 
  onCancel,
  onRefetch
}: ServiceCenterFormProps) => {
  const { mutate: createServiceCenter, isPending: isCreating } = useCreateServiceCenter();
  const { mutate: updateServiceCenter, isPending: isUpdating } = useUpdateServiceCenter();
  const remoteUtilityId = getRemoteUtilityId();
  const isPending = isCreating || isUpdating;
  
  const [selectedCity, setSelectedCity] = useState<string>(center?.cityCode || "");
  const [selectedAreas, setSelectedAreas] = useState<string[]>(center?.areaCodes || []);
  const [selectedSubAreas, setSelectedSubAreas] = useState<string[]>(center?.subAreaCodes || []);

  // API hooks for hierarchical data
  const { data: cityData, isLoading: isCitiesLoading } = useCity({
    remote_utility_id: remoteUtilityId
  });

  const { data: areaData, isLoading: isAreasLoading } = useArea({
    remote_utility_id: remoteUtilityId,
    code_list: selectedCity ? [selectedCity] : []
  });

  const { data: subAreaData, isLoading: isSubAreasLoading } = useSubArea({
    remote_utility_id: remoteUtilityId,
    code_list: selectedAreas.length > 0 ? selectedAreas : []
  });

  const { data: premiseData, isLoading: isPremisesLoading } = usePremise({
    remote_utility_id: remoteUtilityId,
    code_list: selectedSubAreas.length > 0 ? selectedSubAreas : []
  });

  const { data: serviceCenterTypesData, isLoading: isServiceTypesLoading } = useServiceCenters({
    remote_utility_id: remoteUtilityId
  });

  // Transform API data for form options (with null checks)
  const cityOptions = cityData?.result?.map(city => ({
    label: city.name || "",
    value: city.code || ""
  })) || [];

  const areaOptions = areaData?.result?.map(area => ({
    label: area.name || "",
    value: area.code || ""
  })) || [];

  const subAreaOptions = subAreaData?.result?.map(subArea => ({
    label: subArea.name || "",
    value: subArea.code || ""
  })) || [];

  const premiseOptions = premiseData?.result?.map(premise => ({
    label: premise.name || "",
    value: premise.code || ""
  })) || [];

  // Transform service center types from API
  const serviceTypeOptions = serviceCenterTypesData?.result?.map(type => ({
    label: type.value || "",
    value: type.key || ""
  })) || [];

  // Create form fields configuration
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Name",
      required: true,
      placeholder: "Enter service center name...",
      helperText: "A clear, descriptive name for the service center location",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      }
    },
    {
      name: "address",
      type: "text",
      label: "Address",
      required: true,
      placeholder: "Enter street address...",
      helperText: "Complete street address including building number and street name",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "phone",
      type: "text",
      label: "Phone",
      required: true,
      placeholder: "Enter phone number...",
      helperText: "Primary contact number for the service center",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "email",
      type: "email",
      label: "Email",
      required: true,
      placeholder: "Enter email address...",
      helperText: "Official email address for inquiries and support",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      required: true,
      placeholder: "Enter description...",
      helperText: "Brief description of the service center",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        textarea:
    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "type",
      type: "select",
      label: "Service Center Type",
      required: true,
      options: serviceTypeOptions,
      placeholder: isServiceTypesLoading ? "Loading types..." : "-- Select Type --",
      helperText: "Classification of the service center based on its primary function",
     
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "city",
      type: "select",
      label: "City",
      required: true,
      options: cityOptions,
      placeholder: isCitiesLoading ? "Loading cities..." : "-- Select City --",
      helperText: "Select the city where the service center is located",
    
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "areas",
      type: "multi-select",
      label: "Areas",
      required: true,
      options: areaOptions,
      placeholder: isAreasLoading ? "Loading areas..." : "Select areas...",
      helperText: "Select one or more areas where the service center operates",
     
      maxDisplayItems: 2,
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full min-h-16 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "subAreas",
      type: "multi-select",
      label: "Sub Areas",
      required: false,
      options: subAreaOptions,
      placeholder: isSubAreasLoading ? "Loading sub areas..." : "Select sub areas...",
      helperText: "Sub areas within the selected areas",
    
      maxDisplayItems: 2,
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full min-h-16 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "premises",
      type: "multi-select",
      label: "Premises",
      required: false,
      options: premiseOptions,
      placeholder: isPremisesLoading ? "Loading premises..." : "Select premises...",
      helperText: "Select one or more premises where the service center operates",
    
      maxDisplayItems: 3,
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full min-h-16 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    // Individual checkbox fields for each service
    {
      name: "billPayment",
      type: "checkbox",
      label: "Bill Payment",
      required: false,
      classes: {
        container: "mb-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2",
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
    {
      name: "newConnections",
      type: "checkbox", 
      label: "New Connections",
      required: false,
      classes: {
        container: "mb-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2",
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
    {
      name: "disconnections",
      type: "checkbox",
      label: "Disconnections", 
      required: false,
      classes: {
        container: "mb-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2",
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
    {
      name: "complaints",
      type: "checkbox",
      label: "Complaints",
      required: false,
      classes: {
        container: "mb-2", 
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2",
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
    {
      name: "technicalSupport",
      type: "checkbox",
      label: "Technical Support",
      required: false,
      classes: {
        container: "mb-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2", 
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
    {
      name: "documentCollection",
      type: "checkbox",
      label: "Document Collection",
      required: false,
      classes: {
        container: "mb-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2",
        label: "text-sm font-medium text-gray-700 ml-2",
      },
    },
  ];

  // Create form configuration
  const formConfig = {
    fields: formFields,
    mode: "onBlur" as const,
  };

  // Create form using FormService
  const form = FormService.createForm(formConfig);

  // Set default values when editing
  React.useEffect(() => {
    if (center) {
      console.log("Setting form values for center:", center); // Debug log
      
      form.reset({
        name: center.name || "",
        address: center.address || "",
        phone: center.phone || "",
        email: center.email || "",
        description: center.description || "",
        type: center.type || "",
        city: center.cityCode || "",          
        areas: center.areaCodes || [],    
        subAreas: center.subAreaCodes || [],  
        premises: center.premiseCodes || [],   
        // Map services array to individual checkbox fields
        billPayment: center.services?.includes("Bill Payment") || false,
        newConnections: center.services?.includes("New Connections") || false,
        disconnections: center.services?.includes("Disconnections") || false,
        complaints: center.services?.includes("Complaints") || false,
        technicalSupport: center.services?.includes("Technical Support") || false,
        documentCollection: center.services?.includes("Document Collection") || false,
      });
      
      // Set local state for dependent dropdowns
      setSelectedCity(center.cityCode || "");
      setSelectedAreas(center.areaCodes || []);
      setSelectedSubAreas(center.subAreaCodes || []);
    }
  }, [center, form]);

  // Handle field changes for dependent dropdowns
  const handleFieldChange = (fieldName: string, value: any) => {
    try {
      if (fieldName === "city") {
        setSelectedCity(value || "");
        setSelectedAreas([]);
        setSelectedSubAreas([]);
        form.setValue("areas", []);
        form.setValue("subAreas", []);
        form.setValue("premises", []);
      } else if (fieldName === "areas") {
        setSelectedAreas(Array.isArray(value) ? value : []);
        setSelectedSubAreas([]);
        form.setValue("subAreas", []);
        form.setValue("premises", []);
      } else if (fieldName === "subAreas") {
        setSelectedSubAreas(Array.isArray(value) ? value : []);
        form.setValue("premises", []);
      }
    } catch (error) {
      console.error("Error in handleFieldChange:", error);
    }
  };

  function handleSubmit(data: any) {
    console.log("Form submit data:", data); // Debug log

    // Convert individual checkbox fields back to services array
    const services = [];
    if (data.billPayment) services.push("Bill Payment");
    if (data.newConnections) services.push("New Connections");
    if (data.disconnections) services.push("Disconnections");
    if (data.complaints) services.push("Complaints");
    if (data.technicalSupport) services.push("Technical Support");
    if (data.documentCollection) services.push("Document Collection");

    if (!center) {
      // Create payload exactly matching your curl example
      const payload: CreateServiceCenterPayload = {
        city: [data.city],
        area: data.areas || [],
        sub_area: data.subAreas || [],
        premise: data.premises || [],
        utility_service: ["Water"],
        name: data.name,
        email: data.email,
        contact_number: data.phone,
        address: data.address,
        description: data.description || "",
        remote_utility_id: remoteUtilityId,
        type: data.type,
        additional_data: {
          is_bill_payment: data.billPayment || false,
          is_new_connections: data.newConnections || false,
          is_disconnection: data.disconnections || false,
          is_complaints: data.complaints || false,
          is_technical_support: data.technicalSupport || false,
          is_document_collection: data.documentCollection || false,
        }
      };

      createServiceCenter(payload, {
        onSuccess: () => {
          onRefetch?.();
          onSave({ ...data, services });
        },
        onError: (error) => {
          console.error('Failed to create service center:', error);
        }
      });
    } else {
      // Update existing service center with same payload structure
      const updatePayload: CreateServiceCenterPayload = {
        city: [data.city],
        area: data.areas || [],
        sub_area: data.subAreas || [],
        premise: data.premises || [],
        utility_service: ["Water"],
        name: data.name,
        email: data.email,
        contact_number: data.phone,
        address: data.address,
        description: data.description || "",
        remote_utility_id: remoteUtilityId,
        type: data.type,
        additional_data: {
          is_bill_payment: data.billPayment || false,
          is_new_connections: data.newConnections || false,
          is_disconnection: data.disconnections || false,
          is_complaints: data.complaints || false,
          is_technical_support: data.technicalSupport || false,
          is_document_collection: data.documentCollection || false,
        }
      };

      updateServiceCenter({ 
        id: center.id, 
        payload: updatePayload 
      }, {
        onSuccess: () => {
          onRefetch?.();
          onSave({ ...data, services });
        },
        onError: (error) => {
          console.error('Failed to update service center:', error);
        }
      });
    }
  }

  function handleCancel() {
    form.reset();
    setSelectedCity("");
    setSelectedAreas([]);
    setSelectedSubAreas([]);
    onCancel();
  }

  // Custom footer with cancel button
  const renderCustomFooter = () => (
    <div className="flex gap-3 pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        disabled={isPending}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => form.handleSubmit(handleSubmit)()}
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={isPending}
      >
        {isPending ? (center ? "Updating..." : "Creating...") : (center ? "Update" : "Create")} Service Center
      </button>
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{center ? "Edit Service Center" : "Add New Service Center"}</DialogTitle>
          <DialogDescription>
            {center ? "Update the service center details below." : "Create a new service center location."}
          </DialogDescription>
        </DialogHeader>
        
        <div id="service-center-dynamic-form">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            config={{
              ...formConfig,
              showSubmitButton: false,
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
            responsiveColumns={{
              md: 2,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};